import moment from 'moment';
import * as Yup from 'yup';
import { AUDIT_OUTCOMES, AuditCategory, AuditLogQuery, AuditOutcome } from '../../components/services/auditService';

// Which AuditAPI list the page reads. 'global' and 'all' are Globaladmin-only on the API.
export type ActivityLogScope = 'tenant' | 'global' | 'all';

export interface ActivityLogFilterValues {
  /** Local calendar dates (YYYY-MM-DD); empty = the API default (last 30 days ending now). */
  from: string;
  to: string;
  actorEmail: string;
  action: string;
  entity: string;
  entityKey: string;
  category: AuditCategory | '';
  outcome: AuditOutcome | '';
  scope: ActivityLogScope;
  /**
   * Scope 'tenant' only: the tenant picked in the Scope dropdown (Globaladmin and reseller). Empty = the tenant
   * selected in the header.
   */
  tenantId: string;
}

// Mirrors AuditLogQueryLimits on the API.
export const ACTIVITY_LOG_PAGE_SIZE = 50;
/** Most events one CSV download fetches (the API's `limit`, AuditLogQueryLimits.MaxLimit): the newest ones. */
export const ACTIVITY_LOG_EXPORT_LIMIT = 1000;
export const DEFAULT_RANGE_DAYS = 30;
export const MAX_RANGE_DAYS = 366;
export const MAX_FILTER_LENGTH = 512;

const DATE_FORMAT = 'YYYY-MM-DD';
const SCOPES: ActivityLogScope[] = ['tenant', 'global', 'all'];
// 'entity' and 'entityKey' are hidden in the filter form for now (ActivityLogFilterForm.tsx), so they are not read
// from the URL either - an old shared link must not apply a filter the user can't see. Add them back here when the
// fields return.
const TEXT_KEYS = ['actorEmail', 'action' /* , 'entity', 'entityKey' */] as const;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const EMPTY_FILTERS: ActivityLogFilterValues = {
  from: '',
  to: '',
  actorEmail: '',
  action: '',
  entity: '',
  entityKey: '',
  category: '',
  outcome: '',
  scope: 'tenant',
  tenantId: '',
};

const isValidDate = (value: string) => moment(value, DATE_FORMAT, true).isValid();

/** The range the date inputs show when the URL has none — the same 30 days the API applies by default. */
export const defaultDateRange = (now: moment.Moment = moment()) => ({
  from: now.clone().subtract(DEFAULT_RANGE_DAYS, 'days').format(DATE_FORMAT),
  to: now.clone().format(DATE_FORMAT),
});

/**
 * Reads filters from the URL query string. Unknown or malformed values are dropped rather than sent to the API,
 * the global scopes are only honoured for roles allowed to use them, and a chosen tenant only for roles with the
 * Scope dropdown (the AuditAPI still checks access to it).
 */
export const parseFilters = (params: URLSearchParams, canUseGlobalScopes: boolean, canChooseTenant = false): ActivityLogFilterValues => {
  const filters: ActivityLogFilterValues = { ...EMPTY_FILTERS };

  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  if (isValidDate(from)) filters.from = from;
  if (isValidDate(to)) filters.to = to;

  TEXT_KEYS.forEach((key) => {
    filters[key] = (params.get(key) ?? '').trim().slice(0, MAX_FILTER_LENGTH);
  });

  // Category is hidden in the filter form for now, so it is not read from the URL either (see TEXT_KEYS).
  // const category = params.get('category') as AuditCategory | null;
  // if (category && AUDIT_CATEGORIES.includes(category)) filters.category = category; // (re-import AUDIT_CATEGORIES)

  const outcome = params.get('outcome') as AuditOutcome | null;
  if (outcome && AUDIT_OUTCOMES.includes(outcome)) filters.outcome = outcome;

  const scope = params.get('scope') as ActivityLogScope | null;
  if (canUseGlobalScopes && scope && SCOPES.includes(scope)) filters.scope = scope;

  const tenantId = params.get('tenant') ?? '';
  if (canChooseTenant && filters.scope === 'tenant' && GUID.test(tenantId)) filters.tenantId = tenantId.toLowerCase();

  return filters;
};

/** Writes filters to the URL query string; empty values and the default scope are left out. */
export const filtersToSearchParams = (filters: ActivityLogFilterValues): URLSearchParams => {
  const params = new URLSearchParams();
  (['from', 'to', ...TEXT_KEYS, /* 'category', */ 'outcome'] as const).forEach((key) => {
    const value = filters[key].trim();
    if (value) params.set(key, value);
  });
  if (filters.scope !== 'tenant') params.set('scope', filters.scope);
  else if (filters.tenantId) params.set('tenant', filters.tenantId);
  return params;
};

/**
 * The Scope dropdown's value: 'tenant:<id>' for a tenant, or 'global' / 'all'. One select covers both the scope and
 * the tenant, so the form keeps them in step.
 */
export const scopeChoiceOf = (filters: Pick<ActivityLogFilterValues, 'scope' | 'tenantId'>, fallbackTenantId: string | null): string =>
  filters.scope === 'tenant' ? `tenant:${filters.tenantId || fallbackTenantId || ''}` : filters.scope;

export const fromScopeChoice = (choice: string): Pick<ActivityLogFilterValues, 'scope' | 'tenantId'> =>
  choice.startsWith('tenant:') ? { scope: 'tenant', tenantId: choice.slice('tenant:'.length) } : { scope: choice as ActivityLogScope, tenantId: '' };

/** Maps filters to AuditAPI query parameters: local calendar days become an inclusive UTC instant range. */
export const filtersToQuery = (filters: ActivityLogFilterValues): AuditLogQuery => ({
  from: filters.from ? moment(filters.from, DATE_FORMAT, true).startOf('day').toISOString() : undefined,
  to: filters.to ? moment(filters.to, DATE_FORMAT, true).endOf('day').toISOString() : undefined,
  actorEmail: filters.actorEmail || undefined,
  action: filters.action || undefined,
  entity: filters.entity || undefined,
  entityKey: filters.entityKey || undefined,
  category: filters.category || undefined,
  outcome: filters.outcome || undefined,
  pageSize: ACTIVITY_LOG_PAGE_SIZE,
});

const textFilter = Yup.string().trim().max(MAX_FILTER_LENGTH, `Must be at most ${MAX_FILTER_LENGTH} characters`);

// Same rules the API enforces (AuditLogFilterFactory), checked up front so the user gets an inline message.
export const activityLogFilterSchema = Yup.object({
  from: Yup.string().test('date', 'Enter a valid date', (value) => !value || isValidDate(value)),
  to: Yup.string()
    .test('date', 'Enter a valid date', (value) => !value || isValidDate(value))
    .test('order', '"To" must be on or after "From"', function (value) {
      const { from } = this.parent;
      if (!value || !from || !isValidDate(value) || !isValidDate(from)) return true;
      return !moment(value, DATE_FORMAT).isBefore(moment(from, DATE_FORMAT));
    })
    .test('span', `The date range can be at most ${MAX_RANGE_DAYS} days`, function (value) {
      const { from } = this.parent;
      if (!value || !from || !isValidDate(value) || !isValidDate(from)) return true;
      // Inclusive whole days: from 00:00 to the end of "to".
      return moment(value, DATE_FORMAT).diff(moment(from, DATE_FORMAT), 'days') < MAX_RANGE_DAYS;
    }),
  actorEmail: textFilter.email('Enter a valid email address'),
  action: textFilter,
  entity: textFilter,
  entityKey: textFilter,
});
